import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS, API_RESPONSE_LIMITS } from '@app/core/constants/endpoints';
import { List } from '@app/shared/models/list.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Asset } from '@app/shared/models/asset.model';
import { AssetAdapter } from './adapters/asset.adapter';
import { ModelAdapter } from './adapters/model.adapter';
import { environment } from '@environments/environment';
import { TagAdapter } from './adapters/tag.adapter';
import { Tag } from '@app/shared/models/tag.model';
import { CompanyAssetsStatsAdapter } from '@app/core/services/adapters/company-assets-stats.adapter';
import { CompanyAssetsStats } from '@app/shared/models/company-assets-stats.model';
import { HttpClientSkippingInterceptorsService } from './http-client-skipping-interceptors.service';
import { AssetGroupAdapter } from './adapters/asset-group.adapter';
import { CountriesStoreService } from '../stores/countries-store.service';
import { Shift } from '@app/shared/models/shift.model';
import { ShiftAdapter } from '@core/services/adapters/shift.adapter';

@Injectable()
export class CompanyService {
    constructor(
        private http: HttpClient,
        private httpSkippingInterceptors: HttpClientSkippingInterceptorsService,
        private assetAdapter: AssetAdapter,
        private modelAdapter: ModelAdapter,
        private tagAdapter: TagAdapter,
        private companyAssetsStatsAdapter: CompanyAssetsStatsAdapter,
        private assetGroupsAdapter: AssetGroupAdapter,
        private countriesStoreService: CountriesStoreService,
        private shiftAdapter: ShiftAdapter
    ) {}

    getLogo(companyId: number): Observable<Blob> {
        const headers = this.httpSkippingInterceptors.getAuthHeaders();

        return this.httpSkippingInterceptors
            .get(`${ENDPOINTS.apis.companies}/${companyId}/logo`, {
                headers,
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(map((response) => response.body));
    }

    getAssets(companyId: number): Observable<List<Asset>> {
        const params = new HttpParams()
            .set('verbose', String(true))
            .set('limit', API_RESPONSE_LIMITS.all.toString());

        return this.http.get(`${ENDPOINTS.apis.companies}/${companyId}/assets`, { params }).pipe(
            map((list: any) => {
                return {
                    start: list.start,
                    limit: list.limit,
                    size: list.size,
                    links: {
                        previous: list.links.previous,
                        next: list.links.next,
                    },
                    items: list.objects.map((data: any) => {
                        const model = this.modelAdapter.adapt(data.parentModelDetails);
                        const tags = data.assetTags.map((tag) => this.tagAdapter.adapt(tag));
                        const groups = data.assetGroups.map((group) =>
                            this.assetGroupsAdapter.adapt(group)
                        );

                        if (data.location.country) {
                            data.location.country = this.countriesStoreService.getCountry(
                                data.location.country.countryCode
                            );
                        }

                        return this.assetAdapter.adapt({ ...data, model, tags, groups });
                    }),
                } as List<Asset>;
            })
        );
    }

    getTags(companyId: number): Observable<List<Tag>> {
        const params = new HttpParams()
            .set('verbose', String(true))
            .set('limit', API_RESPONSE_LIMITS.default.toString());

        return this.http.get(`${ENDPOINTS.apis.companies}/${companyId}/tags`, { params }).pipe(
            map((list: any) => {
                const items: Tag[] = list.objects.map((data: any) => {
                    return this.tagAdapter.adapt(data);
                });

                items.sort((a, b) => a.name.localeCompare(b.name));

                return {
                    start: list.start,
                    limit: list.limit,
                    size: list.size,
                    links: {
                        previous: list.links.previous,
                        next: list.links.next,
                    },
                    items,
                } as List<Tag>;
            })
        );
    }

    getTag(href: string): Observable<Tag> {
        return this.http
            .get(`${environment.baseUrl}${href}`)
            .pipe(map((data: any) => this.tagAdapter.adapt(data)));
    }

    getStats(companyId: number): Observable<CompanyAssetsStats> {
        return this.http
            .get(`${ENDPOINTS.apis.companies}/${companyId}/stats`)
            .pipe(map((data: any) => this.companyAssetsStatsAdapter.adapt(data)));
    }

    getShifts(companyId: number): Observable<List<Shift>> {
        const params = new HttpParams()
            .set('verbose', String(true))
            .set('limit', API_RESPONSE_LIMITS.all.toString());

        return this.http.get(`${ENDPOINTS.apis.companies}/${companyId}/shifts`, { params }).pipe(
            map((list: any) => {
                return {
                    start: list.start,
                    limit: list.limit,
                    size: list.size,
                    links: {
                        previous: list.links.previous,
                        next: list.links.next,
                    },
                    items: list.objects
                        .map((data: any) => {
                            return this.shiftAdapter.adapt(data);
                        })
                        .sort((a: Shift, b: Shift) =>
                            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                        ),
                } as List<Shift>;
            })
        );
    }
}
